import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  GraduationCap,
  Loader2,
  CheckCircle,
  Briefcase,
  MapPin,
  Send,
  Sparkles,
  Users,
  DollarSign,
  Clock,
  Star,
} from "lucide-react";

const RecruitmentApply = () => {
  const { campaignId } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    linkedin_url: "",
    experience_years: 0,
    experience_summary: "",
    domain_expertise: "",
    motivation: "",
  });

  useEffect(() => {
    fetchCampaign();
  }, [campaignId]);

  const fetchCampaign = async () => {
    try {
      const res = await axios.get(`${API}/recruitment/campaigns/${campaignId}`);
      setCampaign(res.data);
    } catch {
      setCampaign(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.experience_summary.trim() || !form.domain_expertise.trim()) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/recruitment/apply`, {
        ...form,
        campaign_id: campaignId,
      });
      setSubmitted(true);
      toast.success("Candidature envoyée avec succès !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <Briefcase className="w-10 h-10 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-heading font-bold mb-3">Campagne introuvable</h1>
          <p className="text-muted-foreground">
            Cette campagne de recrutement n'existe pas ou a été clôturée.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-lg mx-auto px-4">
          <div className="w-24 h-24 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle className="w-12 h-12 text-primary" />
          </div>
          <h1 className="text-3xl font-heading font-bold mb-4">Candidature envoyée ! 🎉</h1>
          <p className="text-lg text-muted-foreground mb-2">
            Merci <strong>{form.full_name}</strong> pour votre intérêt.
          </p>
          <p className="text-muted-foreground">
            Notre équipe examinera votre profil et vous contactera par email à{" "}
            <strong>{form.email}</strong> pour la suite du processus.
          </p>
          <div className="mt-8 bg-card border border-border rounded-lg p-6">
            <h3 className="font-bold mb-3">Prochaines étapes :</h3>
            <div className="space-y-3 text-sm text-left">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary">1</span>
                </div>
                <span>Examen de votre candidature par notre équipe</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-xs font-bold">2</span>
                </div>
                <span>Entretien vidéo pour évaluer votre expertise</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-xs font-bold">3</span>
                </div>
                <span>Phase d'essai : dépôt de 2 vidéos de démonstration</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-xs font-bold">4</span>
                </div>
                <span>Validation et création de votre espace Instructeur</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="recruitment-apply-page">
      {/* Hero */}
      <div className="relative bg-gradient-to-br from-primary/10 via-card/50 to-purple-500/10 border-b border-border/40 overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-6">
            <GraduationCap className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Recrutement formateurs</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4">
            {campaign.title}
          </h1>
          <div className="flex flex-wrap items-center justify-center gap-4 mb-6">
            <Badge className="bg-primary/20 text-primary border-primary/30 text-base px-4 py-1">
              {campaign.domain}
            </Badge>
            {campaign.candidates_count > 0 && (
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Users className="w-4 h-4" />
                {campaign.candidates_count} candidat{campaign.candidates_count > 1 ? "s" : ""}
              </span>
            )}
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {campaign.description}
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-5 gap-10">
          {/* Benefits sidebar */}
          <div className="lg:col-span-2 order-2 lg:order-1">
            <div className="sticky top-8 space-y-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  Pourquoi nous rejoindre ?
                </h3>
                <div className="space-y-4">
                  {[
                    { icon: DollarSign, title: "Revenus partagés", desc: "Commission attractive sur chaque vente de vos cours" },
                    { icon: Clock, title: "Flexibilité totale", desc: "Créez et gérez vos cours à votre rythme" },
                    { icon: Users, title: "Communauté active", desc: "Rejoignez une communauté de formateurs passionnés" },
                    { icon: Star, title: "Visibilité", desc: "Bénéficiez de notre audience et marketing" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <item.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{item.title}</p>
                        <p className="text-xs text-muted-foreground">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {campaign.requirements && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-primary" />
                    Prérequis
                  </h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">
                    {campaign.requirements}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Application Form */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            <div className="bg-card border border-border rounded-lg p-8">
              <h2 className="text-2xl font-heading font-bold mb-1">Postuler maintenant</h2>
              <p className="text-muted-foreground mb-6">Remplissez le formulaire ci-dessous</p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-1.5 block">Nom complet *</Label>
                    <Input
                      required
                      value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      placeholder="Jean Dupont"
                    />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">Email *</Label>
                    <Input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="jean@example.com"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-1.5 block">Téléphone</Label>
                    <Input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+33 6 12 34 56 78"
                    />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">Profil LinkedIn</Label>
                    <Input
                      type="url"
                      value={form.linkedin_url}
                      onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })}
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-1.5 block">Domaine d'expertise *</Label>
                    <Input
                      required
                      value={form.domain_expertise}
                      onChange={(e) => setForm({ ...form, domain_expertise: e.target.value })}
                      placeholder="Docker, Kubernetes, AWS..."
                    />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">Années d'expérience</Label>
                    <Input
                      type="number"
                      min={0}
                      max={50}
                      value={form.experience_years}
                      onChange={(e) => setForm({ ...form, experience_years: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-1.5 block">Résumé de votre expérience *</Label>
                  <Textarea
                    required
                    value={form.experience_summary}
                    onChange={(e) => setForm({ ...form, experience_summary: e.target.value })}
                    placeholder="Décrivez votre parcours professionnel, vos certifications, vos projets clés..."
                    rows={4}
                  />
                </div>

                <div>
                  <Label className="mb-1.5 block">Motivation</Label>
                  <Textarea
                    value={form.motivation}
                    onChange={(e) => setForm({ ...form, motivation: e.target.value })}
                    placeholder="Pourquoi souhaitez-vous devenir formateur sur notre plateforme ?"
                    rows={3}
                  />
                </div>

                <Button type="submit" className="w-full glow-green text-lg py-6" disabled={submitting}>
                  {submitting ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5 mr-2" />
                  )}
                  Envoyer ma candidature
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  En postulant, vous acceptez que vos informations soient utilisées dans le cadre du processus de recrutement.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecruitmentApply;
